import { styles } from './PercentOffHtmlBanner.css';
import React, { useEffect, useRef } from 'react';
import { ContentThemePalette } from '../Contentful/transformers/ThemeTransformer';
import { ContentfulAssetFile } from '@jbhi-fi/apis/src/contentful/types';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { PercentOffText } from './PercentOffText/PercentOffText';
import { isValid } from 'date-fns';
import { formatDate } from '../ProductHtmlBanner/utils';
import { CategoryText } from './CategoryText/CategoryText';
import { BrandIconsBox } from './BrandIconsBox/BrandIconsBox';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint/useBreakpoint';
import { IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { Link, useNavigate } from '../PlatformFunctionality';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { useTrackingContext } from '../Tracking';
import { TrackingData, trackingDataToDataAttributes } from '../HeroBanner/utils';

const MAX_BRAND_ICONS = 8;

export type PercentOffHtmlBannerProps = {
  percentOff: number;
  categoryName: string;
  brandNames: Array<IconName>;
  disclaimer?: string;
  endDate?: Date;
  url: string;
  displayShopNowButton?: boolean;
  theme?: ContentThemePalette;
  trackingData?: TrackingData;
  backgroundImage: ContentfulAssetFile | undefined;
};

export const PercentOffHtmlBanner = ({
  percentOff,
  categoryName,
  brandNames,
  disclaimer,
  endDate,
  url,
  displayShopNowButton,
  theme = { background: '#F00', heading: palette.primary.light, text: palette.primary.dark },
  trackingData,
}: PercentOffHtmlBannerProps) => {
  const handleNavigate = useNavigate();
  const isMobile = !useBreakpoint('md', 'up');

  const roundedPercentOff = Math.floor(percentOff);

  const anchorRef = useRef<HTMLAnchorElement>(null);
  const isOnScreen = useIsOnScreen(anchorRef, { persistOnceSeen: true });
  const { tracker } = useTrackingContext();

  const haveTrackedImpression = useRef<boolean>(false);
  useEffect(() => {
    if (isOnScreen && !haveTrackedImpression.current) {
      haveTrackedImpression.current = true;
      tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: anchorRef.current });
    }
  }, [isOnScreen, tracker]);

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = () => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: anchorRef.current });
  };

  return (
    <Link
      ref={anchorRef}
      href={url}
      className={styles.linkWrapper}
      onClick={handleClick}
      {...trackingDataToDataAttributes(
        trackingData ? { ...trackingData, category: trackingData.category || tracker.category } : undefined
      )}
    >
      <aside className={styles.container} style={{ backgroundColor: theme.background }}>
        <div className={styles.topSection}>
          <div className={styles.leftSide}>
            <PercentOffText percentOff={roundedPercentOff} color={theme.heading} className={styles.percentOffText} />
          </div>
          <div className={styles.rightSide}>
            {brandNames?.length ? (
              <BrandIconsBox brandNames={brandNames.slice(0, MAX_BRAND_ICONS)} className={styles.brandIconsBox} />
            ) : null}
            <CategoryText
              color={theme.heading}
              className={styles.categoryText}
              maxFontSizePx={isMobile ? 42 : undefined}
            >
              {categoryName}
            </CategoryText>
          </div>
        </div>
        <div className={styles.bottomSection}>
          {disclaimer ? (
            <span className={styles.disclaimer} style={{ color: theme.text }}>
              * {disclaimer}
            </span>
          ) : null}
          <div className={styles.bottomRightSection}>
            {endDate && isValid(endDate) ? (
              <div className={styles.endDateTag({ hasShopNowButton: !!url && displayShopNowButton })}>
                <CalloutFont as="span" className={styles.endDateTagText}>
                  ENDS {formatDate(endDate, true)}
                </CalloutFont>
              </div>
            ) : null}
            {url && displayShopNowButton ? (
              <JBButton
                title={url}
                type="primary"
                onClick={() => handleNavigate(url)}
                label="Shop now"
                fullWidth={false}
                classes={styles.shopNowButton}
              />
            ) : null}
          </div>
        </div>
      </aside>
    </Link>
  );
};
